import React, { useEffect, useState } from 'react';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import imageGenerationService, { type StyleRating } from '../../services/api/imageGenerationService';

interface StyleFeedbackButtonsProps {
  imageId: number;
  styleId?: number | null;
  currentRating?: StyleRating | null;
  size?: 'sm' | 'md';
  source: 'generated_image_card' | 'banner_detail_panel' | 'image_detail_panel';
  onRated?: (rating: StyleRating | null) => void;
}

const StyleFeedbackButtons: React.FC<StyleFeedbackButtonsProps> = ({
  imageId,
  styleId,
  currentRating = null,
  size = 'sm',
  source,
  onRated,
}) => {
  const [rating, setRating] = useState<StyleRating | null>(currentRating);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setRating(currentRating);
  }, [currentRating]);

  if (!styleId) {
    return null;
  }

  const buttonSize = size === 'sm' ? 'h-7 w-7' : 'h-8 w-8';
  const iconSize = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';

  const submit = async (nextRating: StyleRating) => {
    if (isSaving) return;
    const previous = rating;
    const finalRating = previous === nextRating ? null : nextRating;
    setRating(finalRating);
    onRated?.(finalRating);

    try {
      setIsSaving(true);
      if (finalRating) {
        await imageGenerationService.rateGeneratedImageStyle(imageId, finalRating, {
          context: { source },
        });
      } else {
        await imageGenerationService.deleteGeneratedImageStyleRating(imageId);
      }
    } catch (error) {
      setRating(previous);
      onRated?.(previous);
      console.error('Failed to save style feedback:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="inline-flex items-center gap-1" aria-label="スタイル適合評価">
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          submit('good');
        }}
        disabled={isSaving}
        className={`${buttonSize} inline-flex items-center justify-center rounded-md border transition-colors ${
          rating === 'good'
            ? 'border-emerald-500 bg-emerald-50 text-emerald-600'
            : 'border-gray-200 bg-white text-gray-500 hover:border-emerald-300 hover:text-emerald-600'
        } disabled:opacity-60`}
        title="このスタイルは合っている"
      >
        <ThumbsUp className={iconSize} />
      </button>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          submit('bad');
        }}
        disabled={isSaving}
        className={`${buttonSize} inline-flex items-center justify-center rounded-md border transition-colors ${
          rating === 'bad'
            ? 'border-rose-500 bg-rose-50 text-rose-600'
            : 'border-gray-200 bg-white text-gray-500 hover:border-rose-300 hover:text-rose-600'
        } disabled:opacity-60`}
        title="このスタイルは合っていない"
      >
        <ThumbsDown className={iconSize} />
      </button>
    </div>
  );
};

export default StyleFeedbackButtons;
